/**
 * AI МОДУЛЬ — API ↔ АДМИНЫ ГЭРЭЭ (docs/50). Хоёр тал ЗӨВХӨН ЭНЭ файлаас уншина: эрх, алдааны код, SSE-ийн үйл явдал,
 * харилцааны DTO, хэрэглээ/квот, хэрэгслийн нэрийн дүрэм.
 *
 * ! 🔴 ЧАТЫН ТҮВШИН = BI-ИЙН ТҮВШИН (`resolveBiAccess`: Pro > Менежер (≥1 суваг) > Basic). ТУСДАА AI түвшин, эрх, толь
 * ! ҮҮСГЭХГҮЙ — хоёр дахь, зөрүүтэй «хэн мөнгө харах вэ» гэсэн үнэн гарна. Мөнгө/мөрийг AI-д нээдэг зүйл нь BI цэгийн
 * ! серверийн хаалга (`BiTierInterceptor`) — AI зөвхөн асуусан хэрэглэгчийн ТОКЕНООР дууддаг (docs/48 §2.1).
 *
 * ! Anthropic SDK-ийн төрөл ЭНД ОРОХГҮЙ (админ SDK-гүй). SSE-ийн үйл явдал нь манай ӨӨРИЙН, жижиг, тогтвортой хэлбэр —
 * ! Anthropic-ийн түүхий stream event-ийг хөтөч рүү ДАМЖУУЛАХГҮЙ (thinking, signature, tool input JSON задлагдахгүй).
 */

import type { BiManagerBlocked, BiSalesChannelRef } from './bi-tier.js'
import { BI_BOARD_TIERS, type BiTier } from './bi.js'

/* ─────────────────────────── Эрх ─────────────────────────── */

/** Чат ашиглах (өөрийн харилцаа, өөрийн хэрэглээ). Панелын сесс л — апп (`sid`) 403. */
export const AI_CHAT_PERMISSION = 'ai.chat:use' as const

/** Бүх хэрэглэгчийн хэрэглээ/зардал (админ). Мессежийн агуулга ХЭЗЭЭ Ч биш. */
export const AI_USAGE_PERMISSION = 'ai.usage:list' as const

/* ─────────────────────────── Түвшин ─────────────────────────── */

/** Чатын түвшин — BI-ийн түвшинтэй ИЖИЛ толь (`basic` < `manager` < `pro`). */
export type AiTier = BiTier

export const AI_TIERS = BI_BOARD_TIERS

/* ─────────────────────────── Загвар ба анхдагч (API env-ээр дарна) ─────────────────────────── */

/**
 * ЗАГВАР — `AI_MODEL` env-ээр дарна; анхдагч `claude-opus-5` (эзэмшигчийн шийдвэр — зардлын төлөө БУУЛГАХГҮЙ).
 * ! Ялгаатай загварт шилжих нь бүх кэшийг цэвэрлэнэ (кэш загвараар хүрээлэгддэг) — харилцааны дундуур солихгүй.
 */
export const AI_DEFAULT_MODEL = 'claude-opus-5' as const

/** `output_config.effort` — `AI_EFFORT` env; чатын анхдагч `medium` (Opus 5-д low/medium хүчтэй, docs/50 §12). */
export const AI_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const

export type AiEffort = (typeof AI_EFFORTS)[number]

export const AI_DEFAULT_EFFORT: AiEffort = 'medium'

/**
 * ӨДРИЙН КВОТ (УБ-ын хоногоор) — түвшин бүрд мессеж ба токен. API env-ээр дарна:
 * `AI_QUOTA_{BASIC|MANAGER|PRO}_{MESSAGES|TOKENS}`. Хэтэрвэл 429 `AI_QUOTA_EXCEEDED` (турн ЭХЛЭХЭЭС ӨМНӨ).
 * ! Токен = `aiQuotaTokens` (кэшээс уншсан токен ОРОХГҮЙ — урт харилцааг торгохгүй; зардлын тооцоонд орно).
 */
export const AI_DEFAULT_DAILY_QUOTAS: Readonly<Record<AiTier, { messages: number; tokens: number }>> = {
  basic: { messages: 50, tokens: 1_000_000 },
  manager: { messages: 150, tokens: 3_000_000 },
  pro: { messages: 400, tokens: 8_000_000 }
}

/** Хэрэглэгчийн нэг мессежийн урт (тэмдэгт). DTO ба админы composer ИЖИЛ. */
export const AI_MESSAGE_MAX_CHARS = 4000

/** Харилцааны гарчгийн урт (тэмдэгт). */
export const AI_TITLE_MAX_CHARS = 120

/**
 * Гарчиггүй үүсгэсэн харилцааны түр гарчиг. Эхний турн дээр сервер асуултын эхний `AI_AUTO_TITLE_CHARS` тэмдэгтээр
 * солино (`message_end.title`) — нэмэлт загварын дуудлагагүй.
 */
export const AI_DEFAULT_CONVERSATION_TITLE = 'Шинэ харилцаа'

export const AI_AUTO_TITLE_CHARS = 60

/** Нэг харилцаанд хэрэглэгчийн турны дээд тоо (`AI_MAX_TURNS_PER_CONVERSATION`) — хэтэрвэл 409 `AI_CONVERSATION_LIMIT`. */
export const AI_DEFAULT_MAX_TURNS_PER_CONVERSATION = 30

/* ─────────────────────────── Алдааны код ─────────────────────────── */

/**
 * AI-ИЙН АЛДААНЫ КОД — HTTP биеийн `code` ба SSE `error.code`. Админ ЗӨВХӨН кодоор салгана (мессежийн текстээр биш).
 *   `AI_NOT_CONFIGURED`       503 — `ANTHROPIC_API_KEY` алга / хэрэгслийн каталог (`docs/openapi/bi.json`) уншигдсангүй;
 *   `AI_QUOTA_EXCEEDED`       429 — өдрийн мессеж эсвэл токены квот (биед `quota`, `Retry-After` = УБ-ын шөнө дунд хүртэл);
 *   `AI_CONVERSATION_NOT_FOUND` 404 — байхгүй / өөрийнх биш / устгагдсан (IDOR — «байхгүй»-гээс ялгахгүй);
 *   `AI_TURN_IN_PROGRESS`     409 — энэ харилцаанд турн явж байна (нэг зэрэг нэг);
 *   `AI_CONVERSATION_LIMIT`   409 — харилцааны турны дээд тоо — шинэ харилцаа эхлүүл;
 *   `AI_ACCESS_CHANGED`       409 — харилцааны өмнөх турнуудаас хойш хэрэглэгчийн хандалт БУУРСАН/ӨӨРЧЛӨГДСӨН (түвшин,
 *                             Менежерийн суваг, эх эрх) — хуучин хэрэгслийн хариу түүхэнд байгаа тул шинэ харилцаа эхлүүл;
 *   `AI_RATE_LIMITED`         SSE — Anthropic 429 (SDK-ийн дахин оролдлогын дараа ч);
 *   `AI_OVERLOADED`           SSE — Anthropic 529/5xx;
 *   `AI_REFUSED`              SSE — `stop_reason: refusal` (fallback-ийн дараа ч) — хариу хаягдана;
 *   `AI_MAX_TOKENS`           SSE — хэрэгслийн оролт `max_tokens`-оор тасарсан (хэрэгсэл АЖИЛЛУУЛАХГҮЙ);
 *   `AI_MAX_ROUNDS`           SSE — хэрэгслийн тойргийн/дуудлагын төсөв дууссан;
 *   `AI_TIMEOUT`              SSE — турны нийт хугацаа;
 *   `AI_ABORTED`              — хэрэглэгч «Зогсоох» дарсан (холболт тасарсан; хөтөч рүү илгээгдэхгүй, түүхэнд л);
 *   `AI_UPSTREAM_ERROR`       SSE — Anthropic-ийн бусад алдаа (400/401/403/404 — тохиргоо);
 *   `AI_INTERNAL`             SSE/500 — бусад.
 */
export const AI_ERROR_CODES = [
  'AI_NOT_CONFIGURED',
  'AI_QUOTA_EXCEEDED',
  'AI_CONVERSATION_NOT_FOUND',
  'AI_TURN_IN_PROGRESS',
  'AI_CONVERSATION_LIMIT',
  'AI_ACCESS_CHANGED',
  'AI_RATE_LIMITED',
  'AI_OVERLOADED',
  'AI_REFUSED',
  'AI_MAX_TOKENS',
  'AI_MAX_ROUNDS',
  'AI_TIMEOUT',
  'AI_ABORTED',
  'AI_UPSTREAM_ERROR',
  'AI_INTERNAL'
] as const

export type AiErrorCode = (typeof AI_ERROR_CODES)[number]

export const isAiErrorCode = (value: unknown): value is AiErrorCode =>
  typeof value === 'string' && (AI_ERROR_CODES as readonly string[]).includes(value)

/** HTTP алдааны бие (`HttpException(body)`) — SSE эхлэхээс ӨМНӨХ алдаа (401/403/404/409/429/503). */
export type AiErrorBody = {
  statusCode: number
  error: string
  code: AiErrorCode
  message: string

  /** 429 квот — УБ-ын дараагийн шөнө дунд хүртэлх секунд (`Retry-After`-тэй ижил). */
  retryAfterSeconds?: number | null
  quota?: AiQuotaSnapshot
}

/* ─────────────────────────── Хэрэглээ ба квот ─────────────────────────── */

/** Anthropic `usage`-ийн нийлбэр (нэг турны БҮХ тойрог, fallback-ийн `usage.iterations`-ийг оруулаад). */
export type AiUsageTotals = {
  inputTokens: number
  outputTokens: number
  cacheCreationInputTokens: number
  cacheReadInputTokens: number
}

/**
 * КВОТЫН ТОКЕН — кэшгүй оролт + кэшид бичсэн + гаралт. Кэшээс уншсан (≈0.1× үнэтэй) ОРОХГҮЙ: урт харилцаа кэшийн
 * ачаар хямд тул квотыг ч хурдан идэхгүй. Зардлын тооцоо (`costUsd`) бүгдийг үнээр нь тооцно (API).
 */
export const aiQuotaTokens = (usage: AiUsageTotals): number =>
  usage.inputTokens + usage.cacheCreationInputTokens + usage.outputTokens

/** Өнөөдрийн квотын агшин (УБ-ын хоног). */
export type AiQuotaSnapshot = {
  /** УБ-ын өдөр `YYYY-MM-DD`. */
  date: string
  tier: AiTier
  messages: { used: number; limit: number }
  tokens: { used: number; limit: number }

  /** Дараагийн УБ-ын шөнө дунд — ISO UTC `Z` (`2026-09-27T16:00:00.000Z`). */
  resetsAt: string
}

/**
 * `GET /ai/usage` — нэвтэрсэн хүний AI төлөв: тохиргоо, түвшин (BI-ийнхтэй ижил), Менежерийн саад, сувгууд, квот.
 * Админы толгойн тэмдэг, квотын хэмжүүр, «тохируулаагүй» төлөвийн ЦОРЫН ГАНЦ эх.
 */
export type AiMeUsageResponse = {
  /** `false` — `ANTHROPIC_API_KEY` эсвэл хэрэгслийн каталог алга → админ «тохируулаагүй» төлөв (чат илгээхгүй). */
  configured: boolean
  model: string
  tier: AiTier
  managerBlocked: BiManagerBlocked | null
  salesChannels: BiSalesChannelRef[]
  quota: AiQuotaSnapshot
}

/** `GET /ai/admin/usage` мөр — хэрэглэгч × УБ өдөр. Агуулга ОРОХГҮЙ. */
export type AiAdminUsageRow = AiUsageTotals & {
  userId: string
  userName: string
  userEmail: string

  /** УБ-ын өдөр `YYYY-MM-DD`. */
  date: string

  /** Тэр өдрийн СҮҮЛИЙН турны түвшин. */
  tier: AiTier
  messages: number
  toolCalls: number

  /** Тооцоолсон зардал (USD, загварын үнээр — docs/50 §12). Нэхэмжлэл биш. */
  costUsd: number
}

export type AiAdminUsageResponse = {
  from: string
  to: string
  data: AiAdminUsageRow[]
  totals: AiUsageTotals & { messages: number; toolCalls: number; costUsd: number; users: number }
}

/* ─────────────────────────── Харилцаа ─────────────────────────── */

export type AiConversationSummary = {
  id: string
  title: string

  /** ҮҮСГЭХ үеийн түвшин (мэдээлэл) — турн бүр ОДООГИЙН түвшнээр явна. */
  tier: AiTier
  createdAt: string
  updatedAt: string
  lastMessageAt: string | null
}

export type AiConversationListResponse = {
  data: AiConversationSummary[]

  /**
   * Дараагийн хуудасны курсор (тунгалаг бус), эсвэл `null`. Эрэмбэ — сүүлийн идэвх (`updatedAt`: үүсгэх, турн,
   * гарчиг) буурахаар; шинэ хоосон харилцаа ч дээр гарна.
   */
  nextCursor: string | null
}

/** Хэрэгслийн нэг дуудлагын ХАРАГДАЦ (түүх ба SSE). Хэрэгслийн хариуны өгөгдөл хөтөч рүү ОРОХГҮЙ. */
export type AiToolActivity = {
  /** Anthropic `tool_use.id`. */
  id: string

  /** Хэрэгслийн нэр (`aiToolName(routeKey)`). */
  name: string

  /** Каталогийн түлхүүр (`erp.1c-detail.summary`). */
  routeKey: string

  /** Монгол богино шошго — «BI-ээс татаж байна: {label}…» (каталогийн `summary`-ээс). */
  label: string

  /** Аргументын уншигдахуйц товчлол (`from=2026-09-01, to=2026-09-27`), ≤ 200 тэмдэгт. */
  args: string
}

export type AiToolOutcome = {
  id: string

  /** 2xx БА оролт хүчинтэй. */
  ok: boolean

  /** Loopback-ийн HTTP статус; оролт хүчингүй / хугацаа хэтэрсэн бол `null`. */
  status: number | null

  /**
   * Шалтгааны код: API-ийн `code` (`BI_PRO_REQUIRED`, `BI_MANAGER_CHANNEL_REQUIRED` …) эсвэл AI-ийн — оролт
   * `INVALID_INPUT`, `UNKNOWN_TOOL`, төсөв `BUDGET`, хугацаа `TIMEOUT`, HTTP-ээс `FORBIDDEN` (403 кодгүй — эх эрх /
   * модуль унтраалттай), `UNAUTHORIZED` (401 — сесс дууссан), `NOT_FOUND`, `BAD_REQUEST`, `BUSY` (503 1С-ийн ээлж),
   * `UNAVAILABLE` (5xx / холбогдсонгүй) — эсвэл `null` (амжилт).
   */
  code: string | null

  /** Хариуны гол мөрийн тоо (тооцож болвол), эсвэл `null`. */
  rows: number | null

  /** Хариуг хэмжээний хязгаараар ТАЙРСАН (загварт хураангуй очсон). */
  trimmed: boolean

  /** Мөнгөн талбар буулгагдсан (`redacted`) — Basic/Менежерийн хаалга. */
  redacted: boolean

  /**
   * BI-ийн 403 биеийн `unlockTiers` (`['manager','pro']` / `['pro']`) — chip «Менежер эсвэл Pro» / «Pro»-г ялгана.
   * Бусад үед байхгүй.
   */
  unlockTiers?: AiTier[]
}

export type AiMessagePart =
  | { type: 'text'; text: string }
  | ({ type: 'tool' } & AiToolActivity & { outcome: AiToolOutcome | null })

/** Турны төгсгөлийн шалтгаан (Anthropic-ийн `stop_reason` + манай гогцооны төгсгөл). */
export const AI_STOP_REASONS = ['end_turn', 'max_tokens', 'refusal', 'max_rounds', 'timeout', 'aborted', 'error'] as const

export type AiStopReason = (typeof AI_STOP_REASONS)[number]

/**
 * Хөтөчид харагдах мессеж. `system` (турны контекст) ба хэрэгслийн хариуны `user` мөрүүд ХАРАГДАХГҮЙ — туслахын нэг
 * турны (олон тойрог) бүх текст/хэрэгсэл НЭГ `assistant` мессеж болж нэгтгэгдэнэ.
 */
export type AiMessageView = {
  id: string
  role: 'user' | 'assistant'
  createdAt: string
  parts: AiMessagePart[]
  stopReason: AiStopReason | null
}

export type AiConversationDetail = AiConversationSummary & {
  messages: AiMessageView[]

  /**
   * Өмнөх турнуудаас хойш хэрэглэгчийн хандалт буурсан/өөрчлөгдсөн (түвшин, Менежерийн суваг, эх эрх). `true` бол
   * туслахын хариунууд НУУГДАНА (`messages`-т зөвхөн асуултууд), шинэ асуулт 409 `AI_ACCESS_CHANGED` — шинэ харилцаа.
   */
  accessChanged: boolean
}

export type AiCreateConversationBody = { title?: string }

/** `PATCH /ai/conversations/:id` — гарчиг солих (1…`AI_TITLE_MAX_CHARS`). Өөрийнх л; бусдынх 404. */
export type AiUpdateConversationBody = { title: string }

export type AiSendMessageBody = { content: string }

/* ─────────────────────────── SSE ─────────────────────────── */

/**
 * `POST /ai/conversations/:id/messages` → `text/event-stream` (`X-Accel-Buffering: no`, `Cache-Control: no-cache,
 * no-transform`). Кадр: `event: <type>\ndata: <JSON>\n\n`; 15 сек тутам `: ping\n\n` (nginx `proxy_read_timeout` 90с).
 * Урсгал ҮРГЭЛЖ `message_end` ЭСВЭЛ `error`-оор төгсөнө (хэрэглэгч зогсоосноос бусад үед).
 */
export const AI_SSE_EVENT_TYPES = [
  'message_start',
  'text_delta',
  'round_reset',
  'tool_call',
  'tool_result',
  'message_end',
  'error'
] as const

export type AiSseEventType = (typeof AI_SSE_EVENT_TYPES)[number]

export const AI_SSE_HEARTBEAT_MS = 15_000

export type AiSseEvent =
  | {
      type: 'message_start'
      conversationId: string

      /** Хадгалагдсан хэрэглэгчийн мессежийн id (`AiMessageView.id`). */
      userMessageId: string

      /** Энэ турны туслахын мессежийн id (түүхэнд ижил id-аар гарна). */
      messageId: string
      tier: AiTier
      model: string
    }
  | { type: 'text_delta'; text: string }

  /**
   * Сервер ЭНЭ тойргийг дахин эхлүүлж байна (хэрэгслийн оролтын JSON задарсангүй) — хөтөч сүүлийн хэрэгслээс хойших
   * текстийг ХАЯНА (дахин ирнэ). Түүхэнд давхардахгүй.
   */
  | { type: 'round_reset' }
  | ({ type: 'tool_call' } & AiToolActivity)
  | ({ type: 'tool_result' } & AiToolOutcome)
  | {
      type: 'message_end'
      messageId: string
      stopReason: AiStopReason
      usage: AiUsageTotals
      costUsd: number
      quota: AiQuotaSnapshot

      /** Эхний турн дээр сервер үүсгэсэн гарчиг (эс бөгөөс `null`). */
      title: string | null
    }
  | {
      type: 'error'
      code: AiErrorCode
      message: string
      retryAfterSeconds?: number | null
    }

export const isAiSseEventType = (value: unknown): value is AiSseEventType =>
  typeof value === 'string' && (AI_SSE_EVENT_TYPES as readonly string[]).includes(value)

/* ─────────────────────────── Хэрэгслийн нэр ба сонголт ─────────────────────────── */

/**
 * ХЭРЭГСЛИЙН НЭР = каталогийн түлхүүр `[^A-Za-z0-9_]` → `_` (docs/48 §3.1): `erp.1c-detail.summary` → `erp_1c_detail_summary`.
 * Буцааж `routeKey`-ээр тааруулна (регистрийн map). ≤ 64 тэмдэгт (Anthropic-ийн хязгаар).
 */
export const aiToolName = (routeKey: string): string => routeKey.replace(/[^A-Za-z0-9_]/g, '_')

/**
 * ХЭРЭГСЭЛ БОЛОХ цэг (docs/50 §6): `docs/openapi/bi.json`-ийн `GET` + `x-bi.aiUse === 'read'` + түлхүүр `bi.`-ээр
 * ЭХЛЭХГҮЙ (самбар/харагдац/хандалт/Excel — өрөлт, өгөгдөл биш). Бичих цэг (`PUT`/`POST`/`PATCH`/`DELETE`),
 * `confirm`/`never`, каталогт байхгүй зүйл ХЭЗЭЭ Ч хэрэгсэл болохгүй.
 */
export const AI_TOOL_EXCLUDED_ROUTE_PREFIXES = ['bi.'] as const

/**
 * Хэрэгслийн схемд ОРУУЛАХГҮЙ query параметр: `fresh` (1С-ийн кэш тойрох — хэрэглэгч бүрд хязгаартай, AI өөрөө
 * дуудахгүй), `manager` (docs/31 §4w хасагдсан alias → 400 `BI_FILTER_RETIRED`).
 */
export const AI_TOOL_EXCLUDED_PARAMS = ['fresh', 'manager'] as const

/**
 * Хариултад гарах ДОТООД холбоосын зөвшөөрөгдсөн угтвар — админ зөвхөн эдгээрийг `<Link>` болгоно; бусад (гадаад URL,
 * `javascript:`, зураг) ТЕКСТ хэвээр (prompt injection-ээр өгөгдөл гадагш гаргахаас сэргийлнэ, docs/50 §10).
 * BI drill-ийг `parseBiDrillPath`-аар нарийвчлан шалгана.
 */
export const AI_LINK_ALLOWED_PREFIXES = ['/bi/', '/erp/', '/hr/', '/warehouse/', '/delivery/', '/timesheet/'] as const

export const isAiAllowedLink = (href: string): boolean =>
  href.startsWith('/') &&
  !href.startsWith('//') &&
  !/[\s\\]/.test(href) &&
  (AI_LINK_ALLOWED_PREFIXES as readonly string[]).some(prefix => href.startsWith(prefix))
